import type React from 'react';
import { useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';

export function AdminPageShell({
  title,
  backTo,
  actions,
  children,
  stickyTop,
}: {
  title: string;
  backTo?: { href: string; label: string };
  actions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Optional content (e.g. a create form + filter row) pinned to the top of the
   * scroll area together with the page header. When provided, the header and this
   * content stay stuck while the page body scrolls underneath. Omit for a static
   * header. The wrapper bleeds to the container edges so its background covers
   * scrolling content cleanly.
   */
  stickyTop?: React.ReactNode;
}) {
  const header = (
    <div className="flex items-center justify-between mb-6">
      <div>
        {backTo && (
          <Link to={backTo.href} className="text-xs text-vari-gray hover:text-vari-navy mb-1 inline-flex items-center gap-1">
            <span>←</span> {backTo.label}
          </Link>
        )}
        <h1 className="text-xl font-semibold text-vari-navy">{title}</h1>
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {stickyTop !== undefined ? (
        <div className="sticky top-0 z-20 -mx-6 -mt-6 px-6 pt-6 pb-4 bg-gray-50">
          {header}
          {stickyTop}
        </div>
      ) : (
        header
      )}
      {children}
    </div>
  );
}

export const cls = {
  btnPrimary: 'px-4 py-2 bg-vari-navy text-white text-sm rounded-md hover:bg-vari-navy/90 transition-colors font-medium disabled:opacity-50',
  btnSmPrimary: 'px-3 py-1.5 bg-vari-navy text-white text-sm rounded-md hover:bg-vari-navy/90 transition-colors disabled:opacity-50',
  btnGhost: 'px-3 py-1.5 text-sm text-vari-gray hover:text-vari-navy transition-colors',
  btnDanger: 'text-xs text-red-400 hover:text-red-600 transition-colors',
  btnConfirm: 'text-xs px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors',
  btnOutline: 'text-xs px-2 py-1 border border-vari-navy text-vari-navy rounded-md hover:bg-vari-navy hover:text-white transition-colors',
  th: 'text-left px-4 py-3 text-xs font-semibold text-vari-gray uppercase tracking-wide',
  thRight: 'text-right px-4 py-3 text-xs font-semibold text-vari-gray uppercase tracking-wide',
  thCenter: 'text-center px-4 py-3 text-xs font-semibold text-vari-gray uppercase tracking-wide',
  td: 'px-4 py-3 text-sm',
  tdMono: 'px-4 py-3 text-sm font-mono text-vari-gray',
  tdRight: 'px-4 py-3 text-sm text-right',
  tr: (striped: boolean) =>
    `border-b border-gray-100 hover:bg-blue-50/20 transition-colors ${striped ? 'bg-gray-50/50' : ''}`,
  input: 'w-full border border-vari-soft-gray rounded-md px-3 py-1.5 text-sm focus:outline focus:outline-1 focus:outline-vari-navy',
  select: 'border border-vari-soft-gray rounded-md px-3 py-1.5 text-sm focus:outline focus:outline-1 focus:outline-vari-navy bg-white',
  formPanel: 'mb-6 p-4 border border-vari-soft-gray rounded-lg bg-white shadow-sm',
  tableWrap: 'bg-white rounded-lg border border-vari-soft-gray overflow-hidden shadow-sm',
};

/**
 * Presentational drag-and-drop file dropzone: drop or click to pick a single
 * file, then `onFile` fires. Stateless beyond its own drag/hover UI — the parent
 * owns validation, upload, and busy state. (Mirrors the dropzone in
 * CatalogDocumentsPage's TierUploader.)
 */
export function FileDropzone({
  accept,
  busy,
  available,
  onFile,
  idleLabel = 'Drop a file here',
  replaceLabel = 'Drop a file to replace',
}: {
  accept: string;
  busy: boolean;
  available: boolean;
  onFile: (file: File) => void;
  idleLabel?: string;
  replaceLabel?: string;
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onFile(file);
      }}
      onClick={() => inputRef.current?.click()}
      className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
        dragOver ? 'border-vari-navy bg-vari-beige/40' : 'border-vari-soft-gray hover:border-vari-navy/60 bg-gray-50'
      } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
      <p className="text-sm text-vari-navy font-medium">
        {busy ? 'Uploading…' : available ? replaceLabel : idleLabel}
      </p>
      <p className="text-xs text-vari-gray mt-0.5">or click to browse</p>
    </div>
  );
}

export function ChevronDown({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function PencilIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
}

export function TrashIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}
